import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query'
import { ActionBar, RolesIcon, Select, Text } from '@viapeople/components'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useOutletContext, useParams } from 'react-router-dom'
import { type Column, GenericTable } from '../../../../components/GenericTable'
import { useToast } from '../../../../utils/ToastContext'
import { queryClient, trpc, trpcClient } from '../../../../utils/trpc'
import type { CycleRoleListInput, CycleRoleMap } from '../types'

const CycleMapRoles = () => {
	const navigate = useNavigate()
	const { cycleId, organizationId } = useParams<{ cycleId: string; organizationId: string }>()
	const { selectedCustomField, currentStep, markStepCompleted, completed } = useOutletContext<{
		selectedCustomField: string
		currentStep: number
		markStepCompleted: (idx: number) => void
		completed: boolean[]
	}>()
	const [rolesMapped, setRolesMapped] = useState<CycleRoleMap[]>([])
	const prevCustomFieldRef = useRef('')

	useEffect(() => {
		const prev = prevCustomFieldRef.current
		prevCustomFieldRef.current = selectedCustomField
		if (prev && selectedCustomField && prev !== selectedCustomField) {
			setRolesMapped((r) => r.map((role) => ({ ...role, customFieldValue: '' })))
		}
	}, [selectedCustomField])

	const toast = useToast()

	const { data: customFieldValues } = useQuery({
		...trpc.admin.users.getCustomFieldValues.queryOptions({
			organizationId: organizationId as string,
			fieldName: selectedCustomField,
		}),
		enabled: !!selectedCustomField,
	})

	const {
		data: formsData,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
	} = useInfiniteQuery({
		queryKey: ['forms', 'search', organizationId],
		queryFn: async ({ pageParam = 1 }) => {
			const result = await trpcClient.admin.forms.search.query({
				organizationId: organizationId as string,
				page: pageParam,
				perPage: 10,
			})
			return result
		},
		getNextPageParam: (lastPage) => {
			const { page, perPage, total } = lastPage.meta
			const hasMore = page * perPage < total
			return hasMore ? page + 1 : undefined
		},
		initialPageParam: 1,
		enabled: !!organizationId,
	})

	const formOptions = useMemo(() => {
		if (!formsData?.pages) return []
		return formsData.pages.flatMap((page) =>
			page.data.map((form: { name: string; id: string }) => ({
				label: form.name,
				value: form.id,
			})),
		)
	}, [formsData])

	const forms = useMemo(() => {
		if (!formsData?.pages) return undefined
		return {
			data: formsData.pages.flatMap((page) => page.data),
		}
	}, [formsData])

	const columns: Column<CycleRoleMap>[] = [
		{
			field: 'name',
			label: 'Display Name',
			colSpan: 2,
			render: (role: CycleRoleMap) => (
				<Text variant='xsmall' weight='regular' className='group-hover:text-primary-600'>
					{role.name}
				</Text>
			),
		},
		{
			field: 'formId',
			label: 'Form',
			colSpan: 2,
			render: (role: CycleRoleMap) => {
				if (role.formId) {
					const form = forms?.data?.find((form) => form.id === role.formId)
					return (
						<Text variant='xsmall' weight='regular' className='group-hover:text-primary-600'>
							{form?.name || role.formId}
						</Text>
					)
				} else {
					const mappedRole = rolesMapped.find((r) => r.id === role.id)
					return (
						<Select
							key={`${role.id}-form`}
							value={mappedRole?.formId || ''}
							options={formOptions}
							onChange={(value) => {
								const existingRole = rolesMapped.find((r) => r.id === role.id)
								const updatedRole = {
									...role,
									formId: value,
									customFieldValue: existingRole?.customFieldValue || role.fieldValue || '',
								}
								const existingRoleIndex = rolesMapped.findIndex((r) => r.id === role.id)

								if (existingRoleIndex === -1) {
									setRolesMapped((prev) => [...prev, updatedRole])
								} else {
									setRolesMapped((prev) => prev.map((r) => (r.id === role.id ? updatedRole : r)))
								}
							}}
							onMenuScrollToBottom={() => {
								if (hasNextPage && !isFetchingNextPage) {
									fetchNextPage()
								}
							}}
							isLoadingMore={isFetchingNextPage}
							hasMore={hasNextPage}
						/>
					)
				}
			},
		},
		{
			field: 'displayName',
			label: 'Role Display Name',
			colSpan: 2,
			render: (role: CycleRoleMap) => (
				<Text variant='xsmall' weight='regular' className='group-hover:text-primary-600'>
					{role.displayName}
				</Text>
			),
		},
		{
			field: 'description',
			label: 'Description',
			colSpan: 2,
			render: (role: CycleRoleMap) => (
				<Text variant='xsmall' weight='regular' className='group-hover:text-primary-600'>
					{role.description}
				</Text>
			),
		},
		{
			field: 'customFieldValue',
			label: 'Custom Field Value',
			colSpan: 2,
			render: (role: CycleRoleMap) => {
				if (role.fieldValue || completed[1]) {
					return (
						<Text variant='xsmall' weight='regular' className='group-hover:text-primary-600'>
							{role.fieldValue || '-'}
						</Text>
					)
				} else {
					const mappedRole = rolesMapped.find((r) => r.id === role.id)
					return (
						<Select
							key={`${role.id}-${selectedCustomField}`}
							value={mappedRole?.customFieldValue || ''}
							options={
								customFieldValues?.map((value) => ({
									label: value,
									value: value,
								})) ?? []
							}
							onChange={(value) => {
								const existingRole = rolesMapped.find((r) => r.id === role.id)
								const updatedRole = {
									...role,
									customFieldValue: value,
									formId: existingRole?.formId || role.formId || '',
								}
								const existingRoleIndex = rolesMapped.findIndex((r) => r.id === role.id)

								if (existingRoleIndex === -1) {
									setRolesMapped((prev) => [...prev, updatedRole])
								} else {
									setRolesMapped((prev) => prev.map((r) => (r.id === role.id ? updatedRole : r)))
								}
							}}
						/>
					)
				}
			},
		},
	]

	const editRoleMappingMutation = useMutation({
		...trpc.admin.cycles.roles.mappings.edit.mutationOptions({
			onSuccess: () => {
				toast.showToast('Roles mapped')
				navigate(`/organizations/${organizationId}/cycles/${cycleId}/roles/unmappedRoles`)
			},
			onError: () => {
				toast.showToast('Error mapping roles', 'error')
			},
			onSettled: () => {
				queryClient.invalidateQueries({
					queryKey: trpc.admin.cycles.roles.list.queryKey({ cycleId: cycleId as string }),
					exact: false,
					refetchType: 'all',
				})
			},
		}),
	})

	const handleMapRoles = async () => {
		if (!cycleId || !rolesMapped.length) return

		const roles = rolesMapped.map((role: CycleRoleMap) => ({
			id: role.id,
			roleId: role.roleId,
			roleField: selectedCustomField,
			fieldValue: role.customFieldValue,
			formId: role.formId,
		}))

		if (roles.length === 0) return

		await editRoleMappingMutation.mutateAsync({
			cycleId,
			roles: roles as [(typeof roles)[0], ...typeof roles],
		})
	}

	return (
		<div>
			<GenericTable<CycleRoleMap, CycleRoleListInput>
				columns={columns}
				idField='id'
				queryOptionsGenerator={(options) => ({
					...trpc.admin.cycles.roles.list.queryOptions({
						...options,
						cycleId: cycleId as string,
					}),
				})}
				isInternal
				showSearch={false}
				showPagination
				emptyState={{
					icon: RolesIcon,
					title: 'Map Roles',
					description: 'Get started by adding cycle roles',
					actionLabel: 'Add cycle roles',
					onAction: () => {
						navigate(`/organizations/${organizationId}/cycles/${cycleId}/roles`)
					},
				}}
			/>
			{rolesMapped.length !== 0 && currentStep === 1 && (
				<ActionBar
					rightActions={[
						{
							variant: 'secondary',
							label: 'Previous',
							onClick: () => navigate(`/organizations/${organizationId}/cycles/${cycleId}/roles`),
						},
						{
							variant: 'primary',
							label: 'Next',
							onClick: () => {
								markStepCompleted(1)
								handleMapRoles()
							},
						},
					]}
				/>
			)}
		</div>
	)
}

export default CycleMapRoles
